import { useMutation } from '@tanstack/react-query';
import {
  suggestStoreName,
  updateSiteSettings,
  updateComponent,
  publishSite,
  connectPaymentProvider,
} from './endpoints';
import { useMsId, useStoreName } from '../../../store/appStore';
import { extractContactInfo } from '@server/utils/regex';
import { storeNameKeys } from './keys';

interface SuggestStoreNameParams {
  category: string;
  products: string[];
  previousResults: string[];
}

export function useSuggestStoreName() {
  return useMutation<string[], Error, SuggestStoreNameParams>({
    mutationKey: storeNameKeys.mutations.suggestStoreName(),
    mutationFn: ({ category, products, previousResults }) =>
      suggestStoreName(category, products, previousResults),
  });
}

export function useUpdateSiteSettings() {
  const metaSiteId = useMsId();
  const storeName = useStoreName();

  return useMutation({
    mutationKey: storeNameKeys.mutations.updateSiteSettings(),
    mutationFn: () => {
      const insights = {
        storeName: { summary: storeName, status: 'PROVIDED' },
        businessOverview: { summary: '', status: 'PENDING' },
        storeLookAndFeel: { summary: '', status: 'PENDING' },
        contactDetails: { summary: '', status: 'PENDING' },
      };
      return updateSiteSettings(metaSiteId, insights);
    },
    onError: (error) => {
      console.error('Failed to update site settings:', error);
      throw error;
    },
  });
}

export function useUpdateComponent() {
  const metaSiteId = useMsId();
  const storeName = useStoreName();

  return useMutation({
    mutationKey: storeNameKeys.mutations.updateComponent(),
    mutationFn: () => {
      const contactDetails = extractContactInfo('');
      return updateComponent(metaSiteId, storeName, contactDetails);
    },
    onError: (error) => {
      console.error('Failed to update component:', error);
      throw error;
    },
  });
}

export function usePublishSite() {
  const metaSiteId = useMsId();

  return useMutation({
    mutationKey: storeNameKeys.mutations.publishSite(),
    mutationFn: () => publishSite(metaSiteId),
    onError: (error) => {
      console.error('Failed to publish site:', error);
      throw error;
    },
  });
}

export function useConnectPaymentProvider() {
  const metaSiteId = useMsId();

  return useMutation({
    mutationKey: storeNameKeys.mutations.connectPayment(),
    mutationFn: () => connectPaymentProvider(metaSiteId),
    onError: (error) => {
      console.error('Failed to connect payment provider:', error);
      throw error;
    },
  });
}
